import _ from 'lodash';
import { isBlank } from 'underscore.string';
import moment from 'moment';

import { getEnabledV2OrderTypes } from 'shared/core/helpers/dispensaries';
import { defaultDurationEstimates, defaultActionEstimates } from 'shared/constants';
import requestDrivingTrafficFromMapbox from './mapbox';

const calculateDeliveryEstimateWithMapbox = async (
  originLong,
  originLat,
  customerLong,
  customerLat,
  rounded,
  rangeInMinutes,
  defaultDeliveryEstimate
) => {
  if (!isBlank(customerLong) && !isBlank(customerLat)) {
    const origin = [originLong, originLat];
    const destination = [customerLong, customerLat];
    const mapboxDrivingTrafficResponse = await requestDrivingTrafficFromMapbox(origin, destination);
    // represents the travel time from location A to location B in seconds divided by 60 to get minutes
    const deliveryTime = _.get(mapboxDrivingTrafficResponse, `body.durations[0][1]`, 600) / 60;
    const roundedDeliveryTime = Math.ceil(deliveryTime / 5) * 5; // round to nearest 5

    return {
      lowInMinutes: rounded + roundedDeliveryTime,
      highInMinutes: rounded + roundedDeliveryTime + rangeInMinutes,
    };
  }
  return defaultDeliveryEstimate;
};

const memoizedCalculateDeliveryEstimateWithMapbox = _.memoize(calculateDeliveryEstimateWithMapbox);

const getDeliveryEstimate = async ({ actionEstimates, location }, customerLocation, skipMatrixCheck) => {
  const deliveryActionEstimateOrDefault = _.get(actionEstimates, `delivery`, defaultActionEstimates.delivery);
  const baseTimeToPrepareOrder = _.get(deliveryActionEstimateOrDefault, `readyInMinutes`, 25);
  // eslint-disable-next-line max-len
  const defaultDeliveryBuffer = Math.ceil(_.get(deliveryActionEstimateOrDefault, `deliveryTimeInMinutes`, 20) / 5) * 5; // round to nearest 5
  const rangeInMinutes = _.get(deliveryActionEstimateOrDefault, `rangeInMinutes`, 10);
  const roundTo = baseTimeToPrepareOrder > 60 ? 10 : 5;
  let rounded = Math.ceil(baseTimeToPrepareOrder / roundTo) * roundTo; // round to nearest 5 | 10 depending on size

  if (rounded === 0) {
    rounded = 25;
  }

  const defaultDeliveryEstimate = {
    lowInMinutes: rounded + defaultDeliveryBuffer,
    highInMinutes: rounded + defaultDeliveryBuffer + rangeInMinutes,
  };

  if (skipMatrixCheck) {
    return {
      lowInMinutes: rounded,
      highInMinutes: rounded + rangeInMinutes,
    };
  }

  try {
    const [originLong, originLat] = location?.geometry?.coordinates ?? [];
    const [customerLong, customerLat] = [customerLocation.lng, customerLocation.lat];

    return await memoizedCalculateDeliveryEstimateWithMapbox(
      originLong,
      originLat,
      customerLong,
      customerLat,
      rounded,
      rangeInMinutes,
      defaultDeliveryEstimate
    );
  } catch (e) {
    console.error(`Failed to get estimate from mapbox`, e);
  }

  return defaultDeliveryEstimate;
};

const getPickupEstimate = ({ actionEstimates }) => {
  const pickupActionEstimateOrDefault = _.get(actionEstimates, `pickup`, defaultActionEstimates.pickup);
  const baseTimeToPrepareOrder = _.get(pickupActionEstimateOrDefault, `readyInMinutes`, 20);
  const roundTo = baseTimeToPrepareOrder > 60 ? 10 : 5;
  let rounded = Math.ceil(baseTimeToPrepareOrder / roundTo) * roundTo;
  if (rounded === 0) {
    rounded = 20;
  }

  return {
    lowInMinutes: Math.max(rounded - 5, 5),
    highInMinutes: rounded + _.get(pickupActionEstimateOrDefault, `rangeInMinutes`, 5),
  };
};

export const durationEstimatesForOrder = async (dispensary, customerLocation, { skipMatrixCheck } = {}) => {
  let delivery, pickup;
  const deliveryOverride = _.get(dispensary, `durationEstimateOverrides.delivery`, false);
  const pickupOverride = _.get(dispensary, `durationEstimateOverrides.pickup`, false);

  if (deliveryOverride?.enabled) {
    delivery = deliveryOverride;
  } else {
    delivery = await getDeliveryEstimate(dispensary, customerLocation, skipMatrixCheck);
  }

  if (pickupOverride?.enabled) {
    pickup = pickupOverride;
  } else {
    pickup = getPickupEstimate(dispensary);
  }

  return {
    pickup,
    delivery,
  };
};

export const formatEstimate = (actionEstimate, serviceType = `pickup`) => {
  const estimate = actionEstimate || defaultDurationEstimates[serviceType] || defaultDurationEstimates.pickup;
  return `${estimate.lowInMinutes} - ${estimate.highInMinutes} min`;
};

export const getAfterHoursWindow = (
  openInfo, // use return object from openInfoForDispensary()/openInfoForDispensaryV2() in helpers/dispensaries.js
  serviceType = `pickup`
) => {
  if (
    serviceType !== 'delivery' &&
    serviceType !== 'inStorePickup' &&
    serviceType !== 'curbsidePickup' &&
    serviceType !== 'driveThruPickup'
  ) {
    serviceType = `pickup`;
  }

  const { openMoment, closeMoment } = openInfo?.[serviceType]?.nextService || {};

  if (!openMoment || !closeMoment) {
    return {};
  }

  const now = moment();
  const tomorrow = now.clone().add(1, `day`);

  let openDayDisplay;
  if (openMoment.isSame(now, `day`)) {
    openDayDisplay = `today`;
  } else if (openMoment.isSame(tomorrow, `day`)) {
    openDayDisplay = `tomorrow`;
  } else {
    openDayDisplay = openMoment.format(`M/D`);
  }

  const open = openMoment.format(`h:mm a`);
  const close = closeMoment.format(`h:mm a`);

  return {
    day: openDayDisplay,
    open,
    close,
  };
};

export const getCombinedV2PickupHoursWindow = (
  openInfo, // use return object from openInfoForDispensaryV2() in helpers/dispensaries.js
  dispensary
) => {
  const enabledV2OrderTypes = getEnabledV2OrderTypes(dispensary);
  const enabledPickupOrderTypes = _.filter(enabledV2OrderTypes, (orderType) => orderType !== `delivery`);

  // Get the next available opening time for the enabled pickup service types
  const availablePickupOpenTimes = _.compact(
    _.map(enabledPickupOrderTypes, (orderType) => openInfo[orderType]?.nextService.openMoment)
  );

  if (!availablePickupOpenTimes.length > 0) {
    return {};
  }

  const earliestAvailablePickupTime = moment.min(availablePickupOpenTimes);

  // Get the next latest closing time for the enabled pickup service types
  // that falls on the same day as the earliest available pickup time
  const availablePickupCloseTimes = _.filter(
    _.compact(
      _.map(enabledPickupOrderTypes, (orderType) => {
        const closeMoment = openInfo[orderType]?.nextService.closeMoment;
        if (!closeMoment) {
          return null;
        }
        if (closeMoment.format(`h:mm a`) === '12:00 am') {
          // Since midnight closing times are stored as 12:00 am,
          // we need to subtract 1 minute for the correct closing time string.
          return closeMoment.clone().subtract(1, `m`);
        }
        return closeMoment;
      })
    ),
    (closeMoment) => closeMoment.isSame(earliestAvailablePickupTime, `day`)
  );
  const latestAvailablePickupTime = moment.max(availablePickupCloseTimes);

  if (!earliestAvailablePickupTime || !latestAvailablePickupTime) {
    return {};
  }

  const now = moment();
  const tomorrow = now.clone().add(1, `day`);

  let openDayDisplay;
  if (earliestAvailablePickupTime.isSame(now, `day`)) {
    openDayDisplay = `today`;
  } else if (earliestAvailablePickupTime.isSame(tomorrow, `day`)) {
    openDayDisplay = `tomorrow`;
  } else {
    openDayDisplay = earliestAvailablePickupTime.format(`M/D`);
  }

  const open = earliestAvailablePickupTime.format(`h:mm a`);
  const close = latestAvailablePickupTime.format(`h:mm a`);

  return {
    day: openDayDisplay,
    open,
    close,
  };
};

export const formatAfterHoursEstimate = ({ day, open, close }) => {
  if (!day || !open || !close) {
    return `next service opportunity unknown`;
  }
  return `${day}, ${open} - ${close === `12:00 am` ? `11:59 pm` : close}`;
};

export const getServiceEstimateForCart = (Cart) => {
  const serviceType = Cart.order?.deliveryOption === 'delivery' ? 'delivery' : 'pickup';
  const estimate = Cart.isAfterHoursOrder
    ? _.upperFirst(formatAfterHoursEstimate(getAfterHoursWindow(Cart.openInfo, serviceType)))
    : formatEstimate(Cart.durationEstimates[serviceType], serviceType);
  return estimate;
};

export const getServiceEstimateForCartV2 = (Cart) => {
  const serviceType = Cart.order?.deliveryOption === 'delivery' ? 'delivery' : 'pickup';
  const afterHoursServiceType = Cart.order?.deliveryOption === 'pickup' ? 'inStorePickup' : Cart.order?.deliveryOption;

  const estimate =
    Cart.isAfterHoursOrder || !Cart.openInfoForDispensaryV2?.[afterHoursServiceType]?.nextService?.openMoment
      ? _.upperFirst(formatAfterHoursEstimate(getAfterHoursWindow(Cart.openInfoForDispensaryV2, afterHoursServiceType)))
      : formatEstimate(Cart.durationEstimates[serviceType], serviceType);
  return estimate;
};
